import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../context/AppContext";
import api from "../api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { ReceiptText, Landmark, HardDrive, MessageCircle, Mail, Plug, PenLine, ExternalLink, Copy } from "lucide-react";

const ICONS = {
  openapi_sdi: ReceiptText,
  aruba_fe: Landmark,
  google_drive: HardDrive,
  whatsapp_business: MessageCircle,
  smtp_email: Mail,
  twilio_whatsapp: MessageCircle,
  docusign: PenLine,
};

function DocuSignSetup({ t }) {
  const [info, setInfo] = useState(null);
  const [testing, setTesting] = useState(false);
  useEffect(() => {
    api.get(`/docusign/consent-url?origin=${encodeURIComponent(window.location.origin)}`).then((r) => setInfo(r.data)).catch(() => setInfo(null));
  }, []);
  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post("/docusign/test");
      toast.success(`${t("ds.testOk")}: ${r.data.account_name}`);
    } catch (e) { toast.error(e.response?.data?.detail || t("msg.error")); } finally { setTesting(false); }
  };
  if (!info) return null;
  const steps = [t("ds.step1"), t("ds.step2"), t("ds.step3"), t("ds.step4")];
  return (
    <div className="mt-3 space-y-2 rounded-md border border-border bg-muted/30 p-3 text-xs" data-testid="docusign-setup">
      <ol className="list-decimal pl-4 space-y-1 text-muted-foreground">{steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
      <div className="flex items-center gap-1">
        <code className="flex-1 truncate rounded bg-background px-2 py-1 font-mono text-[11px]" data-testid="docusign-redirect-uri">{info.redirect_uri}</code>
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { navigator.clipboard?.writeText(info.redirect_uri); toast.success(t("ds.copied")); }} data-testid="docusign-copy-redirect"><Copy className="h-3.5 w-3.5" /></Button>
      </div>
      <div className="flex flex-wrap gap-2">
        <Badge variant={info.has_ids ? "default" : "secondary"} data-testid="docusign-ids-badge">{t("ds.ids")}: {info.has_ids ? "OK" : "—"}</Badge>
        <Badge variant={info.has_rsa ? "default" : "secondary"} data-testid="docusign-rsa-badge">{t("ds.rsa")}: {info.has_rsa ? "OK" : t("ds.missing")}</Badge>
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        <Button size="sm" variant="outline" className="h-8" onClick={() => window.open(info.url, "_blank", "noopener")} data-testid="docusign-consent-button">
          <ExternalLink className="h-3.5 w-3.5 mr-2" />{t("ds.consent")}
        </Button>
        <Button size="sm" className="h-8" onClick={test} disabled={testing || !info.has_rsa} data-testid="docusign-test-button">
          {testing ? t("common.loading") : t("ds.test")}
        </Button>
      </div>
    </div>
  );
}

export default function Connectors() {
  const { t, user } = useApp();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [folderUrl, setFolderUrl] = useState("");
  const canEdit = ["master", "admin"].includes(user?.role || "master");
  const [smtpTo, setSmtpTo] = useState("");
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [waTo, setWaTo] = useState("");
  const [waTesting, setWaTesting] = useState(false);
  const waTest = async () => {
    setWaTesting(true);
    try {
      const r = await api.post("/connectors/twilio_whatsapp/test", { to: waTo });
      toast.success(`${t("conn.waTestOk")}: ${r.data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("msg.error"));
    } finally { setWaTesting(false); }
  };
  const smtpTest = async () => {
    setSmtpTesting(true);
    try {
      const r = await api.post("/connectors/smtp_email/test", { to: smtpTo });
      toast.success(`${t("conn.smtpTestOk")}: ${r.data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("msg.error"));
    } finally { setSmtpTesting(false); }
  };

  const saveFolder = async () => {
    if (!folderUrl.trim()) return;
    try {
      const r = await api.put("/drive/folder", { folder: folderUrl.trim() });
      toast.success(`${t("conn.drive.folderSaved")}: ${r.data.folder_name}`);
      setFolderUrl("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("msg.error"));
    }
  };

  const load = useCallback(() => {
    api.get("/connectors").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("drive");
    const ds = new URLSearchParams(window.location.search).get("docusign");
    if (p === "connected") { toast.success(t("conn.drive.connectedOk")); load(); }
    else if (p === "error") toast.error(t("conn.drive.error"));
    if (ds === "consent") toast.success(t("ds.consentOk"));
    if (p || ds) window.history.replaceState({}, "", window.location.pathname);
  }, [t, load]);

  const connectDrive = async () => {
    try {
      const r = await api.get("/drive/connect");
      window.location.href = r.data.authorization_url;
    } catch {
      toast.error(t("conn.drive.error"));
    }
  };

  const openConfig = (c) => {
    setActive(c);
    setForm({ environment: "demo", ...c.config });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/connectors/${active.id}`, { config: form });
      toast.success(t("msg.saved"));
      setActive(null);
      load();
    } catch {
      toast.error(t("msg.error"));
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async (id) => {
    try {
      if (id === "google_drive") await api.post("/drive/disconnect");
      else await api.delete(`/connectors/${id}`);
      toast.success(t("msg.deleted"));
      load();
    } catch {
      toast.error(t("msg.error"));
    }
  };

  return (
    <div className="space-y-6" data-testid="connectors-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
          <Plug className="h-7 w-7 text-primary" />{t("nav.connectors")}
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{t("conn.subtitle")}</p>
        <p className="text-xs text-muted-foreground/70 mt-1 max-w-2xl">{t("conn.sharedNote")}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((c) => {
          const Icon = ICONS[c.id] || Plug;
          return (
            <Card key={c.id} data-testid={`connector-card-${c.id}`} className="flex flex-col">
              <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-base">{t(`conn.${c.id}.name`)}</CardTitle>
                </div>
                {c.connected
                  ? <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/30" data-testid={`connector-status-${c.id}`}>{t("conn.connected")}</Badge>
                  : <Badge variant="secondary" data-testid={`connector-status-${c.id}`}>{t("conn.notConnected")}</Badge>}
              </CardHeader>
              <CardContent className="flex-1 flex flex-col justify-between gap-4">
                <div>
                  <CardDescription className="text-sm">{t(`conn.${c.id}.desc`)}</CardDescription>
                  {c.id === "google_drive" && c.connected && c.config?.email && (
                    <p className="text-xs text-muted-foreground mt-2" data-testid="connector-email-google_drive">{c.config.email}</p>
                  )}
                  {canEdit && c.id === "google_drive" && c.connected && (
                    <div className="mt-2 space-y-1.5">
                      {c.config?.folder && (
                        <p className="text-xs text-muted-foreground" data-testid="connector-folder-google_drive">
                          {t("conn.drive.folder")}: <span className="font-medium text-foreground">{c.config.folder}</span>
                        </p>
                      )}
                      <div className="flex gap-2">
                        <Input className="h-8 text-xs" placeholder={t("conn.drive.folderPlaceholder")} value={folderUrl}
                               onChange={(e) => setFolderUrl(e.target.value)} data-testid="drive-folder-input" />
                        <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={saveFolder} data-testid="drive-folder-save">
                          {t("common.save")}
                        </Button>
                      </div>
                    </div>
                  )}
                  {canEdit && c.id === "docusign" && c.connected && <DocuSignSetup t={t} />}
                  {canEdit && c.id === "twilio_whatsapp" && c.connected && (
                    <div className="mt-3 flex items-center gap-2" data-testid="wa-test-box">
                      <Input className="h-8 text-xs" placeholder="+39…" value={waTo} onChange={(e) => setWaTo(e.target.value)} data-testid="wa-test-to-input" />
                      <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={waTest} disabled={waTesting} data-testid="wa-test-button">
                        {waTesting ? t("common.loading") : t("conn.waTest")}
                      </Button>
                    </div>
                  )}
                  {canEdit && c.id === "smtp_email" && c.connected && (
                    <div className="mt-3 flex items-center gap-2" data-testid="smtp-test-box">
                      <Input className="h-8 text-xs" placeholder={user?.email} value={smtpTo} onChange={(e) => setSmtpTo(e.target.value)} data-testid="smtp-test-to-input" />
                      <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={smtpTest} disabled={smtpTesting} data-testid="smtp-test-button">
                        {smtpTesting ? t("common.loading") : t("conn.smtpTest")}
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  {canEdit && (<>
                  {c.id === "google_drive" ? (
                    !c.connected && (
                      <Button size="sm" data-testid="connector-connect-google_drive" onClick={connectDrive}>
                        {t("conn.connect")}
                      </Button>
                    )
                  ) : (
                    <Button size="sm" variant={c.connected ? "outline" : "default"} data-testid={`connector-configure-${c.id}`} onClick={() => openConfig(c)}>
                      {t("conn.configure")}
                    </Button>
                  )}
                  {c.connected && (
                    <Button size="sm" variant="ghost" className="text-destructive" data-testid={`connector-disconnect-${c.id}`} onClick={() => disconnect(c.id)}>
                      {t("conn.disconnect")}
                    </Button>
                  )}
                  </>)}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{active && t(`conn.${active.id}.name`)}</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-4">
              {active.fields.map((f) => (
                <div key={f.key}>
                  <Label>{t(`fld.${f.key}`)}</Label>
                  {f.key === "environment" ? (
                    <Select value={form[f.key] || "demo"} onValueChange={(v) => setForm((prev) => ({ ...prev, [f.key]: v }))}>
                      <SelectTrigger data-testid={`connector-field-${f.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="demo" data-testid="env-option-demo">Demo (test)</SelectItem>
                        <SelectItem value="production" data-testid="env-option-production">Produzione</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : f.key === "rsa_private_key" ? (
                    <Textarea rows={6} className="font-mono text-[11px]"
                      value={form[f.key] === "••••••••" ? "" : (form[f.key] || "")}
                      placeholder={form[f.key] === "••••••••" ? t("conn.secretSet") : "-----BEGIN RSA PRIVATE KEY-----\n...\n-----END RSA PRIVATE KEY-----"}
                      onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      data-testid={`connector-field-${f.key}`} />
                  ) : (
                    <Input
                      type={f.secret ? "password" : "text"}
                      value={form[f.key] === "••••••••" ? "" : (form[f.key] || "")}
                      placeholder={f.secret && form[f.key] === "••••••••" ? t("conn.secretSet") : ""}
                      onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      data-testid={`connector-field-${f.key}`}
                    />
                  )}
                  {f.secret && active.config?.[f.key] === "••••••••" && (
                    <p className="text-[11px] text-muted-foreground mt-1">{t("conn.secretSet")}</p>
                  )}
                </div>
              ))}
              <Button className="w-full" onClick={save} disabled={saving} data-testid="connector-save-button">
                {saving ? t("common.loading") : t("common.save")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
